/** useEvalRunHistory — load an agent's past eval runs, most recent first.
 *
 * Mirrors useTestCases: direct entity-API call, no optimistic bookkeeping.
 */

import { useCallback, useEffect, useState } from 'react';
import type { EvalRunSummary } from '@/entities/eval';
import { getEvalApi } from './evalClient';

export interface UseEvalRunHistoryResult {
  runs: EvalRunSummary[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useEvalRunHistory(agentId: string): UseEvalRunHistoryResult {
  const [runs, setRuns] = useState<EvalRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      setRuns(await getEvalApi().listRuns(agentId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load eval runs');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { runs, loading, error, reload };
}
